// attachment-storage-io.ts — Supabase Storage I/O for attachments (notes + txn receipts).
// Split out of attachment-storage.ts to keep that module under the size cap and to
// isolate the network layer. Private bucket "attachments"; objects are namespaced by
// `${userId}/...` so owner-only RLS can key off the first path segment.
//
// All functions fail soft (return null / no-op) so callers fall back to inline storage;
// they never throw uncaught.

import type { NoteAttachment } from "../types";

const BUCKET = "attachments";

type StorageClient = {
  storage: {
    from: (bucket: string) => {
      upload: (path: string, file: File, opts?: Record<string, unknown>) => Promise<{ error: unknown }>;
      createSignedUrl: (path: string, expiresIn: number) => Promise<{ data: { signedUrl: string } | null; error: unknown }>;
      remove: (paths: string[]) => Promise<{ error: unknown }>;
    };
  };
};

let cached: StorageClient | null = null;

/**
 * Lazily build a Supabase client that reuses the persisted auth session (so Storage
 * RLS sees the signed-in user). Mirrors the ai-runtime client pattern. Returns null
 * when env vars are missing. Cached to avoid multiple GoTrueClient instances.
 */
async function getStorageClient(): Promise<StorageClient | null> {
  if (cached) return cached;
  try {
    const { createClient } = await import("@supabase/supabase-js");
    const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
    if (!url || !anon) return null;
    cached = createClient(url, anon, {
      auth: { persistSession: true, autoRefreshToken: false },
    }) as unknown as StorageClient;
    return cached;
  } catch {
    return null;
  }
}

/**
 * Object path for an attachment. The FIRST segment MUST be the userId — owner-only
 * RLS is `(storage.foldername(name))[1] = auth.uid()`. userId comes from the session,
 * never from client input.
 */
export function buildAttachmentStoragePath(
  userId: string,
  ownerKind: "note" | "transaction",
  ownerId: string,
  attachmentId: string,
  filename: string,
): string {
  const safeName = filename.replace(/[#[\]*?\s]/g, "_");
  return `${userId}/${ownerKind}/${ownerId}/${attachmentId}_${safeName}`;
}

/**
 * Upload a file to the private bucket. Returns a persistable NoteAttachment ref
 * (path + metadata, no blob, no signed url) or null on any failure.
 */
export async function uploadAttachment(
  file: File,
  userId: string,
  ownerKind: "note" | "transaction",
  ownerId: string,
): Promise<NoteAttachment | null> {
  const client = await getStorageClient();
  if (!client) return null;
  const attachmentId = crypto.randomUUID();
  const path = buildAttachmentStoragePath(userId, ownerKind, ownerId, attachmentId, file.name);
  const { error } = await client.storage
    .from(BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type });
  if (error) return null;
  return {
    id: attachmentId,
    name: file.name,
    size: file.size,
    type: file.type,
    path,
    uploadedAt: new Date().toISOString(),
  };
}

/** Fetch a short-lived signed URL for a private object. Null on failure. */
export async function getSignedUrl(path: string, expiresInSec = 3600): Promise<string | null> {
  const client = await getStorageClient();
  if (!client) return null;
  const { data, error } = await client.storage.from(BUCKET).createSignedUrl(path, expiresInSec);
  if (error || !data) return null;
  return data.signedUrl;
}

/** Best-effort bulk delete of objects. Never throws. */
export async function deleteAttachmentObjects(paths: string[]): Promise<void> {
  const real = paths.filter(Boolean);
  if (real.length === 0) return;
  const client = await getStorageClient();
  if (!client) return;
  try {
    await client.storage.from(BUCKET).remove(real);
  } catch {
    // best-effort — orphaned objects acceptable (e.g. offline delete)
  }
}
